<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	//Formatting
	import { format } from 'svelte-i18n';

	//Design system components
	import {
		Alert,
		Button,
		Container,
		Modal,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system';

	//Helpers
	import { is_gen_2 } from 'src/helpers/interface_helpers';
	import { getDeviceIP } from 'src/helpers/actions/webhooks/core/webhook_devices_helpers';
	import { createActionJob } from 'src/helpers/actions/local/core/local_action_helpers';
	import { capFirstChar, sortOn } from 'src/helpers/utilities';
	import { filterDevicesForActionList } from 'src/helpers/actions/webhooks/view/webhook_devices_list_helpers';
	import { setURLParams } from 'src/helpers/actions/local/core/local_action_parse_url_helpers';

	//Types
	import type { Device } from 'src/types/Device';
	import type { ActionJob } from 'src/types/requests/schedules/schedules_g2';

	//Components
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import LocalActions from 'src/components/DevicesSettings/LocalActions/LocalActions.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let allDevices: Device[] = null;
	export let device: Device;
	export let action: ActionJob;

	const dispatchEvent = createEventDispatcher();

	let showModal = false;

	let allDeviceList = sortOn(allDevices.slice(), 'ip');

	let allCardsRefs: { [key: string | number]: HTMLElement } = {};

	let activeStepIndex = 0;
	let localActionsRef: LocalActions;
	let disableSaveBtn = false;

	$: isLastStepWizard = activeStepIndex == 1;
	$: mappedListDevicesByRoomID = filterDevicesForActionList(allDeviceList);

	const onSelect = (device: Device) => {
		let url = '';
		const ip = getDeviceIP(device);
		if (is_gen_2(device)) {
			url = setURLParams(createActionJob(device), device.ip).replace('a2=1&', '');
		} else {
			url = `http://${ip}/${device.category}/${device.channel}?${
				device.category == 'roller' ? 'go=open' : 'turn=off'
			}`;
		}
		dispatchEvent('onSelect', { url, device });
	};

	function handleSave() {
		const result = localActionsRef.save();
		if (!result) return;

		showModal = false;
		setTimeout(() => dispatchEvent('save', result), durationFadeInOut);
	}

	onMount(() => {
		showModal = true;
	});

	const durationFadeInOut = 300;
	const onClose = () => {
		showModal = false;
		activeStepIndex = 0;
		setTimeout(() => dispatchEvent('close'), durationFadeInOut);
	};
</script>

<Modal
	title={$format('button_add_url_device_action', {
		default: 'Add action from my devices',
	})}
	{showModal}
	on:close={onClose}
	removeScrollPadding
>
	<Wizard slot="toolbar" activeStepNumber={activeStepIndex} stepsTotal={2}>
		<WizardItem
			on:click={() => {
				activeStepIndex = 0;
			}}
			active={activeStepIndex === 0}
			title={$format('label_select_device', {
				default: 'Select device',
			})}
			indicatorColor={$showNewLayout ? 'color-2' : undefined}
		/>
		<WizardItem
			on:click={() => {
				activeStepIndex = 1;
			}}
			active={activeStepIndex === 1}
			disabled={device === null}
			title={$format('schedule_select_local_action', {
				default: 'Select Local Action',
			})}
			indicatorColor={$showNewLayout ? 'color-2' : undefined}
		/>
	</Wizard>

	<div class:flex-overflow-hidden={false} slot="content">
		<WizardContainer {activeStepIndex} adaptiveHeight={true}>
			<WizardContent active={activeStepIndex === 0} class="pl-0 pr-0 pb-s">
				<Container padding="scrollPaddings">
					{#if mappedListDevicesByRoomID.length}
						<!-- DO NOT REMOVE COMMENT: Padding references -->
						<div class="pt-s" />
						<SelectList
							title={$format('label_selected', {
								default: 'Selected:',
							})}
							titleHighlight={device ? 1 : 0}
							variant="radio"
							list={mappedListDevicesByRoomID}
							selectedCardsId={device && [{ id: device?.id }]}
							bind:allCardsRefs
							on:click={(e) => onSelect(e.detail)}
						/>
					{:else}
						<Alert
							class="text-center mb-s width-full"
							type="warning"
							heading={capFirstChar(
								$format('no_devices_found', {
									default: 'no devices found.',
								}),
							)}
						/>
					{/if}
				</Container>
			</WizardContent>

			<WizardContent active={activeStepIndex === 1} class="pl-0 pr-0">
				<Container padding="scrollPaddings">
					{#if device}
						{#key action}
							<LocalActions
								bind:this={localActionsRef}
								bind:disableSaveBtn
								{device}
								{action}
							/>
						{/key}
						<!-- DO NOT REMOVE COMMENT: Padding references -->
						<div class="pb-s" />
					{/if}
				</Container>
			</WizardContent>
		</WizardContainer>
	</div>

	<div slot="footer" class="d-flex justify-content-end">
		{#if activeStepIndex > 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => {
					if (activeStepIndex > 0) {
						activeStepIndex--;
					}
				}}
			>
				{$format('button_back', { default: 'Back' })}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			disabled={device === null || (disableSaveBtn && isLastStepWizard)}
			on:click={() => {
				if (isLastStepWizard) {
					handleSave();
				} else {
					activeStepIndex++;
				}
			}}
		>
			{isLastStepWizard
				? $format('button_save', { default: 'Save' })
				: $format('button_next', { default: 'Next' })}
		</Button>
	</div>
</Modal>
